import React, { createContext, useContext, useState, useCallback } from "react";
import { Toast, PendingDeletion } from "../types";

/**
 * Calculates toast display duration based on message length and toast type.
 * Longer messages get more time, error toasts stay 1.5x longer.
 *
 * @param message - The toast message content
 * @param type - The toast type (success, error, info, undo, warning)
 * @returns Duration in milliseconds (min 3000ms, max 10000ms)
 */
const calculateDuration = (
  message: string,
  type: Toast["type"]
): number => {
  const baseTime = 3000; // 3 seconds minimum
  const perCharTime = 50; // 50ms per character
  const errorMultiplier = (type === "error" || type === "warning") ? 1.5 : 1;

  const calculated = baseTime + message.length * perCharTime;
  return Math.min(calculated * errorMultiplier, 10000); // Max 10 seconds
};

interface ToastContextType {
  toasts: Toast[];
  pendingDeletions: PendingDeletion[];
  addToast: (toast: Omit<Toast, "id">) => string;
  removeToast: (id: string) => void;
  addUndoToast: (
    item: any,
    originalIndex: number,
    onUndo: () => void,
    onComplete: () => void
  ) => string;
  addPendingDeletion: (deletion: PendingDeletion) => void;
  removePendingDeletion: (itemId: string) => void;
  getPendingDeletion: (itemId: string) => PendingDeletion | undefined;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

let toastIdCounter = 0;
const generateToastId = () => `toast-${++toastIdCounter}`;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [pendingDeletions, setPendingDeletions] = useState<PendingDeletion[]>(
    []
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (toast: Omit<Toast, "id">): string => {
      const id = generateToastId();
      const newToast: Toast = { ...toast, id };

      setToasts((prev) => [...prev, newToast]);

      // Auto-remove toast after duration (explicit or calculated from message length)
      const duration =
        toast.duration ?? calculateDuration(toast.message, toast.type);
      setTimeout(() => {
        removeToast(id);
      }, duration);

      return id;
    },
    [removeToast]
  );

  const addPendingDeletion = useCallback((deletion: PendingDeletion) => {
    setPendingDeletions((prev) => [...prev, deletion]);
  }, []);

  const removePendingDeletion = useCallback((itemId: string) => {
    setPendingDeletions((prev) => {
      const deletion = prev.find((d) => d.itemId === itemId);
      if (deletion) {
        // Clear the timer
        clearTimeout(deletion.timerId);
      }
      return prev.filter((d) => d.itemId !== itemId);
    });
  }, []);

  const addUndoToast = useCallback(
    (
      item: any,
      originalIndex: number,
      onUndo: () => void,
      onComplete: () => void
    ): string => {
      const toastId = generateToastId();
      const duration = 7000; // 7 seconds

      // Get item name for display
      const itemName =
        item.categoryOption?.name || item.customTitle || "pozycję";

      const toast: Toast = {
        id: toastId,
        message: `Usunięto "${itemName}"`,
        type: "undo",
        action: {
          label: "Cofnij",
          onClick: () => {
            onUndo();
            removeToast(toastId);
            // Remove from pending deletions
            removePendingDeletion(item.id);
          },
        },
        duration,
      };

      setToasts((prev) => [...prev, toast]);

      // Set up auto-completion timer
      const timerId = setTimeout(() => {
        onComplete();
        removeToast(toastId);
        removePendingDeletion(item.id);
      }, duration);

      // Add to pending deletions
      const pendingDeletion: PendingDeletion = {
        itemId: item.id,
        item,
        originalIndex,
        deletedAt: Date.now(),
        timerId,
        toastId,
      };

      addPendingDeletion(pendingDeletion);

      return toastId;
    },
    [removeToast, addPendingDeletion, removePendingDeletion]
  );

  const getPendingDeletion = useCallback(
    (itemId: string): PendingDeletion | undefined => {
      return pendingDeletions.find((d) => d.itemId === itemId);
    },
    [pendingDeletions]
  );

  const value: ToastContextType = {
    toasts,
    pendingDeletions,
    addToast,
    removeToast,
    addUndoToast,
    addPendingDeletion,
    removePendingDeletion,
    getPendingDeletion,
  };

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
};
